<template>
  <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="onCreate">
    <v-card tile>
      <v-card-title class="grey lighten-2"
        >Lägg till tågavvikelser från Traind</v-card-title
      >

      <v-card-text class="mt-2 pa-0">
        <MasterdataTableContainer
          show-select
          v-model="selected"
          :default-table-height="800"
          :loading="fetching"
          :headers="headers"
          :table-data="tableData"
          item-key="_uuid"
        >
          <v-menu
            slot="left"
            v-model="dateDialoag"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                rounded
                filled
                v-model="dateRangeText"
                dense
                :hide-details="true"
                v-on="on"
                v-bind="attrs"
                readonly
                prepend-inner-icon="mdi-calendar-range"
                class="mr-4"
              ></v-text-field>
            </template>
            <v-date-picker
              first-day-of-week="1"
              v-model="dates"
              range
            ></v-date-picker>
          </v-menu>
        </MasterdataTableContainer>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer />
        <v-btn rounded @click="closeModal">Avbryt</v-btn>
        <v-btn
          rounded
          type="submit"
          color="primary"
          :disabled="!selected.length"
          :loading="createPending"
          >Lägg till markerade</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
import MasterdataTableContainer from '@/modules/masterdata/components/MasterdataTableContainer'
import { sync, get } from 'vuex-pathify'
import { required } from '@/common/validationRules'
import orderBy from 'lodash.orderby'
import { format, addDays } from 'date-fns'
import trainDeviationTypes from '@/common/trainDeviationTypes'
import { v4 as uuidv4 } from 'uuid'
import gql from 'graphql-tag'

export default {
  name: 'TrainDeviationImportFromTraind',

  components: {
    MasterdataTableContainer
  },

  data: () => ({
    required,
    dateDialoag: false,
    dates: [
      format(addDays(new Date(), -1), 'yyyy-MM-dd'),
      format(new Date(), 'yyyy-MM-dd')
    ],
    valid: true,
    createPending: false,
    fetching: false,
    selected: [],
    unhandledTrainDeviations: [],
    headers: [
      {
        text: 'Avvikelse',
        value: '_deviation_type'
      },
      {
        text: 'Type',
        value: 'type'
      },
      {
        text: 'Tågnr.',
        value: 'train_number'
      },
      {
        text: 'Teknisktnr.',
        value: 'technical_number'
      },
      {
        text: 'Från',
        value: '_from'
      },
      {
        text: 'Till',
        value: '_to'
      },
      {
        text: 'Avg.',
        value: '_departure'
      },
      {
        text: 'Ank.',
        value: '_arrival'
      }
    ]
  }),

  computed: {
    stops: get('event/stops'),
    model: sync('trainDeviation/model'),
    selectedEventUUID: get('event/selectedUUID'),

    datesSorted() {
      return orderBy(this.dates, [x => x], ['asc'])
    },

    dateRangeText() {
      return this.dates.join(' ~ ')
    },

    tableData() {
      const findLocationName = signature => {
        const location = this.stops.find(
          x => x.short_name === signature.toUpperCase()
        )
        return location ? location.stop_name : signature.toUpperCase()
      }
      return orderBy(
        (this.unhandledTrainDeviations || []).map(x => {
          return {
            ...x,
            _uuid: uuidv4(),
            _event: x.trainDeviation?.event?.title || '',
            _from: findLocationName(x.from),
            _to: findLocationName(x.to),
            _deviation_type: trainDeviationTypes[x.deviation_type],
            _departure: format(new Date(x.departure), 'yyyy-MM-dd HH:mm'),
            _arrival: format(new Date(x.arrival), 'yyyy-MM-dd HH:mm')
          }
        }),
        ['departure'],
        ['desc']
      ).filter(x => !x._event.length)
    }
  },

  watch: {
    dates: {
      handler: 'getUnhandledTrainDeviations',
      immediate: true
    }
  },

  methods: {
    async getUnhandledTrainDeviations() {
      if (this.datesSorted.length !== 2) return
      this.fetching = true
      const [from, to] = this.datesSorted
      this.unhandledTrainDeviations = await this.$store.dispatch(
        'linkDeviation/fetchStatelessUnhandledTrainDeviations',
        {
          from,
          to,
          district_id: this.$store.get('app/selectedDistrictId')
        }
      )
      this.fetching = false
    },

    async onCreate() {
      this.createPending = true

      for (let item of this.selected) {
        const model = {
          uuid: uuidv4(),
          event_uuid: this.selectedEventUUID,
          ...Object.keys(item)
            .filter(key => !key.startsWith('_') && key !== 'trainDeviation')
            .reduce((acc, next) => {
              acc[next] = item[next]
              return acc
            }, {})
        }
        await this.createTrainDeviation({
          ...model,
          checked: true
        })
      }

      this.createPending = false

      this.closeModal()
    },

    async createTrainDeviation(payload) {
      const payloadCopy = { ...payload }
      delete payloadCopy.technical_number

      const { data } = await this.$apollo.mutate({
        mutation: gql`
          mutation($payload: CreateTrainDeviationInput!) {
            createTrainDeviation(payload: $payload) {
              train_deviation_uuid
              train_deviation {
                event_uuid
                uuid
                type
                train_number
                from
                to
                departure
                arrival
                deviation_type
                checked
                deleted
              }
            }
          }
        `,
        variables: {
          payload: payloadCopy
        }
      })

      this.$store.set(
        `trainDeviation/data@${data.createTrainDeviation.train_deviation_uuid}`,
        data.createTrainDeviation.train_deviation
      )
    },

    closeModal() {
      this.$store.commit('trainDeviation/resetModel')
      this.$emit('close')
    }
  }
}
</script>
